import { Fragment } from 'react';
import dayjs from 'dayjs';

import type { StockSummary } from '@/api/reports';
import type { PrintLetterhead } from '@core/api/bookings';
import { formatAmount } from '@core/format/money';
import { costShown, stockCell, stockColumns, stockFootnote } from '@core/reports/stockSummary';

import { documentClass, type PaperSize } from './paper';

/**
 * Report B2 on paper — the stock summary: the shop, the day it stands on, then every category and
 * design with its sizes and colours underneath, the same columns as the screen. On paper nothing is
 * folded: a printed stock list is read whole, on a desk or against the rail. Narrowed to a category
 * it prints what the screen shows, named under the date. Web only, through `PrintFrame`, as the
 * garments in and out report is.
 */
export function StockSummarySheet({
  paper,
  summary,
  letterhead,
  branchName,
}: {
  paper: PaperSize;
  /** As the screen sees it (`inScope`): `scope` names the category it was narrowed to. */
  summary: StockSummary & { scope: string | null };
  letterhead: PrintLetterhead | undefined;
  branchName: string;
}) {
  const columns = stockColumns(summary);
  const withCost = summary.atCost !== null;
  const symbol = letterhead?.currencySymbol ?? '₹';
  const money = (line: { atCost: number | null; total: number; withoutCost: number }) => {
    const cost = costShown(line);

    return cost === null ? '—' : `${symbol}${formatAmount(cost)}`;
  };
  const categories = summary.categories;
  const footnote = stockFootnote(summary);

  return (
    <article id="printableCard" className={`${documentClass(paper)} px-7 py-6`}>
      <header className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <p className="text-[1.35em] leading-tight font-bold">
            {letterhead?.tenantName ?? branchName}
          </p>
          <p className="text-[0.85em]">{letterhead?.branchName ?? branchName}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[1.05em] font-bold tracking-[0.16em] uppercase">Stock summary</p>
          <p className="text-[0.85em] text-[color:var(--doc-muted)]">
            As on {dayjs(summary.asOf).format('D MMMM YYYY')}
          </p>
          {summary.scope && <p className="text-[0.85em] font-semibold">{summary.scope} only</p>}
        </div>
      </header>

      <table className="mt-5 w-full border-collapse text-[0.9em]">
        <thead>
          <tr className="border-b border-[color:var(--doc-rule-strong)] text-right text-[0.85em] text-[color:var(--doc-muted)]">
            <th className="py-1.5 pr-2 text-left font-semibold">Design · size and colour</th>
            {columns.map((column) => (
              <th key={column.key} className="py-1.5 pl-2 font-semibold">
                {column.label}
              </th>
            ))}
            <th className="py-1.5 pl-2 font-semibold">Total</th>
            {withCost && <th className="py-1.5 pl-2 font-semibold">At cost</th>}
          </tr>
        </thead>
        {categories.map((category) => (
          <tbody key={category.categoryId} className="break-inside-avoid-page">
            <tr className="border-b border-[color:var(--doc-rule-strong)] text-right font-bold">
              <td className="pt-3 pb-1 pr-2 text-left text-[0.85em] tracking-[0.06em] uppercase">
                {category.name || 'No category'}
              </td>
              {columns.map((column) => (
                <td key={column.key} className="pt-3 pb-1 pl-2 font-mono">
                  {column.value(category.counts)}
                </td>
              ))}
              <td className="pt-3 pb-1 pl-2 font-mono">{category.total}</td>
              {withCost && (
                <td className="pt-3 pb-1 pl-2 font-mono">
                  {money({
                    ...category,
                    withoutCost: category.designs.reduce((sum, d) => sum + d.withoutCost, 0),
                  })}
                </td>
              )}
            </tr>
            {category.designs.map((design) => (
              <Fragment key={design.itemId}>
                <tr className="border-b border-[color:var(--doc-rule)] text-right font-semibold break-inside-avoid">
                  <td className="py-1 pr-2 text-left">
                    {design.name} <span className="font-mono font-normal">{design.code}</span>
                  </td>
                  {columns.map((column) => (
                    <td key={column.key} className="py-1 pl-2 font-mono">
                      {stockCell(column.value(design.counts))}
                    </td>
                  ))}
                  <td className="py-1 pl-2 font-mono">{design.total}</td>
                  {withCost && <td className="py-1 pl-2 font-mono">{money(design)}</td>}
                </tr>
                {design.variants.map((variant) => (
                  <tr
                    key={variant.variantId}
                    className="border-b border-[color:var(--doc-rule)] text-right text-[0.92em] break-inside-avoid"
                  >
                    <td className="py-0.5 pr-2 pl-4 text-left">{variant.label || 'One size'}</td>
                    {columns.map((column) => (
                      <td key={column.key} className="py-0.5 pl-2 font-mono">
                        {stockCell(column.value(variant.counts))}
                      </td>
                    ))}
                    <td className="py-0.5 pl-2 font-mono">{variant.total}</td>
                    {withCost && <td className="py-0.5 pl-2 font-mono">{money(variant)}</td>}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        ))}
        {summary.scope === null && (
          <tfoot>
            <tr className="border-t-2 border-[color:var(--doc-rule-strong)] text-right font-bold">
              <td className="py-1.5 pr-2 text-left">All garments</td>
              {columns.map((column) => (
                <td key={column.key} className="py-1.5 pl-2 font-mono">
                  {column.value(summary.counts)}
                </td>
              ))}
              <td className="py-1.5 pl-2 font-mono">{summary.total}</td>
              {withCost && <td className="py-1.5 pl-2 font-mono">{money(summary)}</td>}
            </tr>
          </tfoot>
        )}
      </table>

      <p className="mt-4 text-[0.8em] text-[color:var(--doc-muted)]">
        Ready is on the rail with nothing promised; booked is on the rail and promised to a booking
        or hold. The workroom is the cleaner, the tailor, and garments waiting to be checked.
        {footnote ? ` ${footnote}` : ''}
      </p>
    </article>
  );
}
